/* ManageDeviceViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageDeviceViewRenderer");
const PagingDeviceFeed = require("./PagingDeviceFeed");
const _ = require("lodash");

class ManageDeviceViewComponent extends EventEmitter {
    constructor(planDetails) {
        super();
        this._planDetails = planDetails;
        this._planDetails.childViewOpen = true;
        this._gameEditor = planDetails.getGameEditor();
        this._modalUserFeedback = planDetails._modalUserFeedback;
        this._pageSize = 100;
        this._gridKey = "manage_device";
        this._logger = planDetails._logger;
        this._plan = planDetails.getPlan();
        // SDK entity class (injected into the details view): needed for the static update()
        this._OdmOsUpgradePlanEntity = planDetails._OdmOsUpgradePlan;
        this._isDirty = false;
        this._selectedIDs = undefined;
        this._userGridData = undefined;
        this._deviceFeedSystemQuery = {deviceRawCategory: "iot"};
        /*
        //to be reinstated in PI11
        this._deviceFeedSystemQuery = {
            $and: [{deviceRawCategory: "iot"}, {offlineDiscoveryStatus: "DISCOVERED"}],
        };
        */
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    shutdown() {
        return this.isDirtyChecked()
            .then(() =>
                Promise.all([
                    this._availableDevicesPagingFeed
                        ? this._availableDevicesPagingFeed.shutdown()
                        : undefined,
                    this._selectedDevicesPagingFeed
                        ? this._selectedDevicesPagingFeed.shutdown()
                        : undefined,
                ])
            )
            .then(() => {
                this.emit("Shutdown");
                this._unregisterListeners();
                this._availableDevicesPagingFeed = undefined;
                this._selectedDevicesPagingFeed = undefined;
                this._selectedIDs = undefined;
                this._userGridData = undefined;
                this._OdmOsUpgradePlanEntity = undefined;
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get isDirty() {
        return this._isDirty;
    }

    set isDirty(newValue) {
        this._isDirty = newValue;
        return true;
    }
    get selectedIDs() {
        return this._selectedIDs;
    }

    set selectedIDs(newValue) {
        this._selectedIDs = newValue;
        return true;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get getPlanDetailsView() {
        return this._plan;
    }

    // PE-13026 - added additional Properties
    get availColumnConfig() {
        return {
            name: {
                label: "Name",
                entityColumn: "name",
                width: 200,
                priority: 1,
            },
            description: {
                label: "Description",
                entityColumn: "description",
                width: 150,
                priority: 2,
            },
            model: {
                label: "SKU",
                entityColumn: "model",
                width: 100,
                priority: 3,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
                priority: 4,
            },
            manufacturer: {
                label: "Manufacturer",
                shown: true,
                width: 100,
                priority: 5,
            },
            deviceType: {
                label: "Device Type",
                shown: true,
                width: 100,
                priority: 6,
            },
            model: {
                label: "Model",
                shown: true,
                width: 100,
                priority: 7,
            },
            osName: {
                label: "OS Name",
                shown: true,
                width: 220,
                priority: 8,
            },
            osFamily: {
                label: "OS Family",
                shown: true,
                width: 100,
                priority: 9,
            },
            osReleaseName: {
                label: "OS Release Name",
                shown: true,
                width: 100,
                priority: 10,
            },
            osVersion: {
                label: "OS Version",
                shown: true,
                width: 100,
                priority: 11,
            },
            machineArchitecture: {
                label: "Machine Architecture",
                shown: true,
                width: 150,
                priority: 12,
            },
            language: {
                label: "Language",
                shown: true,
                width: 100,
                priority: 13,
            },
        };
    }

    // PE-13026 - added additional Properties
    get selColumnConfig() {
        return {
            name: {
                label: "Name",
                entityColumn: "name",
                width: 200,
                priority: 1,
            },
            description: {
                label: "Description",
                entityColumn: "description",
                width: 150,
                priority: 2,
            },
            model: {
                label: "SKU",
                entityColumn: "model",
                width: 100,
                priority: 3,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
                priority: 4,
            },
            manufacturer: {
                label: "Manufacturer",
                shown: true,
                width: 100,
                priority: 5,
            },
            deviceType: {
                label: "Device Type",
                shown: true,
                width: 100,
                priority: 6,
            },
            model: {
                label: "Model",
                shown: true,
                width: 100,
                priority: 7,
            },
            osName: {
                label: "OS Name",
                shown: true,
                width: 220,
                priority: 8,
            },
            osFamily: {
                label: "OS Family",
                shown: true,
                width: 100,
                priority: 9,
            },
            osReleaseName: {
                label: "OS Release Name",
                shown: true,
                width: 100,
                priority: 10,
            },
            osVersion: {
                label: "OS Version",
                shown: true,
                width: 100,
                priority: 11,
            },
            machineArchitecture: {
                label: "Machine Architecture",
                shown: true,
                width: 150,
                priority: 12,
            },
            language: {
                label: "Language",
                shown: true,
                width: 100,
                priority: 13,
            },
        };
    }

    get availableTargetsPagingFeed() {
        return this._availableDevicesPagingFeed;
    }
    get selectedTargetsPagingFeed() {
        return this._selectedDevicesPagingFeed;
    }
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    wasFeedPreviouslyInitialized() {
        return this._availableDevicesPagingFeed.wasPreviouslyInitialized();
    }

    whenModelIsReady() {
        return this._planDetails._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData =
                    this._planDetails._storageFactory.getRemoteStorageInstanceForEntityGrid(
                        "entity_grid_gl",
                        `${this._gridKey}_available`
                    );
            })
            .then(() => {
                this._createAvailPagingFeed();
                this._createSelPagingFeed();
                return Promise.resolve();
            })
            .then(() => {
                return Promise.all([
                    this._getWorkTargetIds(),
                    this._availableDevicesPagingFeed.whenFeedReady(),
                    this._selectedDevicesPagingFeed.whenFeedReady(),
                ]);
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this.shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getWorkTargetIds() {
        return this._plan
            .loadRelatedDevices(undefined, {id: true}, undefined, undefined)
            .then((results) => {
                if (results.length > 0) {
                    this._selectedIDs = results.map((result) => {
                        return result.id;
                    });
                } else {
                    this._selectedIDs = [];
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageDeviceViewComponent._getWorkTargetIds() error."
                );
                this._selectedIDs = [];
                return Promise.reject(err);
            });
    }
    _createAvailPagingFeed() {
        if (this._availableDevicesPagingFeed) {
            this._availableDevicesPagingFeed.shutdown();
        }
        this._availableDevicesPagingFeed = this._createPagingFeed(
            this.availColumnConfig,
            "$nin",
            `${this._gridKey}_available`,
            true
        );
    }

    _createSelPagingFeed() {
        if (this._selectedDevicesPagingFeed) {
            this._selectedDevicesPagingFeed.shutdown();
        }
        this._selectedDevicesPagingFeed = this._createPagingFeed(
            this.selColumnConfig,
            "$in",
            `${this._gridKey}_selected`,
            false
        );
    }

    _createPagingFeed(columnConfig, mongoIdOperator, feedKey, isFilter) {
        return new PagingDeviceFeed({
            activeOrgId: this._gameEditor.activeOrgId,
            entityDef: this._gameEditor.getDeviceEntity(),
            entityType: "Device",
            columnConfig: columnConfig,
            pageSize: this._pageSize,
            mongoIdOperator: mongoIdOperator,
            sort: {name: 1},
            filter: isFilter
                ? _.get(this._userGridData.getBasicSearchData(), "filterQuery", {})
                : {},
            systemQuery: this._deviceFeedSystemQuery,
            feedKey,
        });
    }
    _saveDevice() {
        const progress = this._startSaveProgress();
        return this._plan
            .updateRelationships()
            .then(() => progress.whenShown)
            .then(() => {
                // Relationships are saved: detach the progress line, then swap the veil content
                // in place (the feedback widget swaps without hiding) for the validation phase.
                progress.complete();
                return this._modalUserFeedback.displayLoading({message: "Validating plan..."});
            })
            .then(() => this._plan.runValidatePlanWork({skipStatusUpdates: true}, {}))
            .then(() => this._updateServerStatusToDraft())
            .then((result) => {
                this._markDraftStatusAsSaved();
                return this._modalUserFeedback.hideDisplayed().then(() => result);
            })
            .catch((err) => {
                progress.stop();
                this._logger.warn({error: err}, "ManageDeviceViewComponent.saveDevice() error.");
                return progress.whenShown.then(() => {
                    this._modalUserFeedback.displayError(err);
                    return Promise.reject(err);
                });
            });
    }

    /**
     * Unlike OsUpgradePlan, the OdmOsUpgradePlan SDK entity has no updateRelationships()
     * override, so the base call leaves the server-side status untouched. Push DRAFT with a
     * targeted static update rather than plan.update(), which would also flush property edits
     * pending on the parent details form.
     */
    _updateServerStatusToDraft() {
        return this._OdmOsUpgradePlanEntity.update(
            this._plan.tenantId,
            this._plan.type,
            this._plan.id,
            {
                status: this._plan.getStatusValues().DRAFT,
            }
        );
    }

    /**
     * Shows the save veil and drives its progress line from the parent view's
     * related-device feed while the backend applies the relationship changes.
     *
     * @returns {{whenShown: Promise, stop: function, complete: function}}
     */
    _startSaveProgress() {
        const feed = _.get(this, "_planDetails._pagingRelatedFeed");
        const goal = _.size(this._selectedIDs);
        const start = _.get(feed, "totalEntities", goal);
        const span = Math.abs(goal - start);
        let lastPct = 0;
        let onRefresh;

        const whenShown = this._modalUserFeedback.displayLoading({
            message: "Saving device changes...",
        });

        const showPct = (pct) => {
            lastPct = Math.max(lastPct, Math.min(100, pct));
            this._modalUserFeedback.showLoadingProgress(`${lastPct}% complete`);
        };

        // Adds and removes that cancel out leave the total unchanged, so no
        // percentage can be derived: keep the spinner-only veil in that case.
        if (feed && span > 0) {
            onRefresh = () => {
                const remaining = Math.abs(goal - feed.totalEntities);
                showPct(Math.round((1 - remaining / span) * 100));
            };
            feed.on("RefreshLinesNumber", onRefresh);
            whenShown.then(() => showPct(lastPct));
        }

        const stop = () => {
            if (onRefresh) {
                feed.removeListener("RefreshLinesNumber", onRefresh);
                onRefresh = undefined;
            }
        };

        return {
            whenShown,
            stop,
            complete: () => {
                stop();
                if (span > 0) {
                    showPct(100);
                }
            },
        };
    }

    _discardDeviceChanges() {
        this._plan.clearPendingRelationships();
        let pristineStatus = _.get(this._plan, "pristineProperties.status");
        if (!_.isUndefined(pristineStatus)) {
            this._plan.setStatus(pristineStatus);
        }
    }

    _markDraftStatusAsSaved() {
        let draftStatus = this._plan.getStatusValues().DRAFT;
        if (_.get(this._plan, "pristineProperties.status") === draftStatus) {
            return;
        }
        this._plan._setPristineProperty("status", draftStatus);
        this._plan.setStatus(draftStatus);
    }

    // OdmOsUpgradePlan has no addDevicesToPlan()/removeDevicesFromPlan(): queue the
    // relationship ops and mark the plan DRAFT locally, as the OSM helpers do.
    _removeDevices(ids) {
        ids.forEach((id) => {
            this._plan.removeFromDevicesRelationship(id);
        });
        this._plan.setStatus(this._plan.getStatusValues().DRAFT);
        return Promise.resolve();
    }
    _addDevices(ids) {
        ids.forEach((id) => {
            this._plan.addToDevicesRelationship(id);
        });
        this._plan.setStatus(this._plan.getStatusValues().DRAFT);
        return Promise.resolve();
    }
    isDirtyChecked() {
        if (this.isDirty) {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    dontHide: true,
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this._saveDevice().then(() => undefined); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (_.get(err, "userRejected")) {
                        return Promise.resolve()
                            .then(() => {
                                this._discardDeviceChanges();
                            })
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => undefined);
                    }
                    // Save failed: _saveDevice() already showed the error veil. Reject so
                    // the view stays open instead of navigating away from the error.
                    return Promise.reject(err);
                });
        } else {
            return Promise.resolve();
        }
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = ManageDeviceViewComponent;
